import { Link, useLocation } from 'react-router-dom'
import {
  FiHome,
  FiFileText,
  FiTerminal,
  FiImage,
  FiSettings,
  FiActivity,
  FiBook,
  FiPhoneCall,
  FiMessageCircle,
  FiCalendar,
  FiUsers,
  FiTrendingUp,
  FiTarget,
  FiX,
} from 'react-icons/fi'

// Nav model — grouped, so the markup below stays declarative and every link
// gets identical active-state and drawer-closing behaviour.
const NAV = [
  {
    heading: 'MAIN MENU',
    items: [
      { to: '/', label: 'Dashboard', icon: FiHome },
      { to: '/templates', label: 'Templates', icon: FiFileText },
      { to: '/test-api', label: 'Test WABA APIs', icon: FiTerminal },
      { to: '/media', label: 'Media Handler', icon: FiImage },
      { to: '/settings', label: 'Settings', icon: FiSettings },
      { to: '/logs', label: 'Analytics', icon: FiActivity },
      { to: '/scheduler', label: 'Scheduler', icon: FiCalendar },
    ],
  },
  {
    heading: 'LEADS SECTION',
    items: [
      { to: '/leads-overview', label: 'Leads Management', icon: FiUsers },
      { to: '/whatsapp-calling', label: 'Whatsapp Calling', icon: FiPhoneCall },
      // Invite a lead by number; chat with them once they tap Yes.
      { to: '/whatsapp-chat', label: 'Whatsapp Chat', icon: FiMessageCircle },
    ],
  },
  {
    // Was a second "LEADS SECTION" heading — these are the ads pages.
    heading: 'ADS SECTION',
    items: [
      // Campaign Manager was a separate page; it is now the Manage view inside
      // Whatsapp Ads Management, so a second entry would be the same screen
      // reached two ways.
      { to: '/whatsapp-ads', label: 'Whatsapp Ads Management', icon: FiTrendingUp },
      { to: '/google-ads', label: 'Google Ads Management', icon: FiTrendingUp },
    ],
  },
  {
    heading: 'AI TOOLS',
    items: [
      { to: '/ai-ocr', label: 'Test AI OCR API', icon: FiBook },
      // Every lead the AI Lead Scoring API has scored, by date range.
      { to: '/lead-scoring', label: 'Lead Scoring', icon: FiTarget },
    ],
  },
]

// Longest-prefix match so /google-ads/summary still highlights Google Ads.
function isActive(pathname, to) {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * Off-canvas drawer below lg, static column at lg and up.
 * `open` / `onClose` are only meaningful on small screens.
 */
export default function Sidebar({ open = false, onClose = () => {} }) {
  const { pathname } = useLocation()

  return (
    <>
      {/* Backdrop — mobile only, click to dismiss */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col
          border-r bg-white transition-transform duration-200 ease-out
          lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-start justify-between px-5 pt-5 lg:px-6 lg:pt-6">
          <div className="flex flex-1 flex-col items-center gap-1">
            <img src="/logo.png" alt="Logo" className="h-14 w-40 object-contain lg:h-20 lg:w-48" />
            {/* The logo directly above already carries the pink; the wordmark
                in black is the same pairing the logo itself uses, and clears
                the contrast bar that pink-on-white does not at this size. */}
            <h1 className="text-lg font-bold tracking-tight text-ink lg:text-2xl">
              MAA PLATFORM
            </h1>
          </div>

          {/* Close button — mobile only */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 lg:hidden"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* The nav is taller than a phone screen, so it scrolls independently */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 pb-6 pt-4 lg:px-6">
          {NAV.map((group) => (
            <div key={group.heading}>
              <div className="mb-2 mt-6 px-2 text-xs font-medium uppercase tracking-wide text-gray-500 first:mt-0">
                {group.heading}
              </div>

              {group.items.map(({ to, label, icon: Icon }) => {
                const active = isActive(pathname, to)
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={onClose}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-[#F96168]/10 text-[#D1272F]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="shrink-0 text-lg" />
                    <span className="truncate">{label}</span>
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}
